/**
 * Centralized auth navigation. Every UI surface that signs the user in or
 * out goes through these two functions — components never reach for SWA
 * URLs (`/.auth/login/aad`, `/.auth/logout`) or `oidc-client-ts` APIs
 * directly.
 *
 * Why: post Ping migration, the app drives sign-in/out through
 * `oidc-client-ts`'s `signinRedirect` / `signoutRedirect` against
 * `auth.corelogic.com`. Centralising those calls here keeps every entry
 * point coherent and lets the unit suite assert call shape without booting
 * the OIDC UserManager.
 *
 * `signIn` accepts an optional `returnUrl` so the anon → /auth/login → Ping →
 * deep-link round-trip preserves the visitor's original target, AND so
 * mid-session 401 re-auths preserve the user's current location. Callers
 * pass `encodeReturnUrl(location)`; this function decodes once via
 * `safeDecodeReturnUrl` before handing the value to `signinRedirect`'s
 * `state`. One encode each direction — `main.tsx`'s `initOidc` restores the
 * decoded state on the way back via `restoreDeepLink`. Rejected/unset → no
 * `state` is passed, and the caller falls back to the default landing page.
 *
 * Two-callsite invariant: `AuthedLayout` (boot-time unauth on the authed
 * sub-tree) and `AuthDriver` (mid-session 401, mounted once in `App`)
 * are the ONLY components that call this function. The idempotency gate is
 * the module `_redirecting` flag in `lib/oidc.ts` (replacing MSAL's
 * `inProgress` machine) — `signinRedirect` navigates the page away, so the
 * only realistic double-fire is a React StrictMode re-invoke before
 * navigation tears the page down. `usePrincipal` consumed by ~20
 * components is pure-read post-refactor; do NOT re-add a `signIn`
 * effect inside it (the fan-out re-creates the bug class).
 *
 * Both functions are async: `signinRedirect`/`signoutRedirect` return a
 * Promise that resolves once the browser has begun the redirect. Callers
 * fire-and-forget via `void signIn()` / `void signOut()` in event handlers —
 * navigation happens inside the function, never on its return.
 */

import { redirectToSignIn, redirectToSignOut } from '@/lib/oidc';
import { safeDecodeReturnUrl } from './return-url';

/**
 * Triggers an `oidc-client-ts` `signinRedirect` against `auth.corelogic.com`.
 * Optional `returnUrl` is validated via `safeDecodeReturnUrl` and
 * round-tripped through the UserManager's `state`, restored post-Ping via
 * `main.tsx`'s `restoreDeepLink`. Delegates to `lib/oidc.ts`'s
 * `redirectToSignIn`, which owns the `_redirecting` idempotency flag.
 */
export async function signIn(returnUrl?: string): Promise<void> {
  const safeReturnUrl = safeDecodeReturnUrl(returnUrl);
  await redirectToSignIn(safeReturnUrl);
}

/**
 * Triggers an `oidc-client-ts` `signoutRedirect` that returns the user to
 * the SPA's `/auth/logout` confirmation page post-Ping-logout. The
 * path-scoped URL (rather than the bare origin) mirrors the `/auth/callback`
 * convention so operators see one consistent auth namespace in the app reg
 * config.
 */
export async function signOut(): Promise<void> {
  await redirectToSignOut();
}
